/**
 * Hierarchy Helpers
 *
 * Purpose: Pure utility functions for hierarchical field operations
 * Architecture: Framework-agnostic helper functions for tree algorithms and path resolution
 * Author: Development Team
 * Created: 2025-01-21
 *
 * Functions:
 * - buildHierarchyChain: Traverse hierarchy from target field to root
 * - valueToTreeNode: Convert MasterDataValue to TreeNode
 * - buildTreeItems: Convert TreeNode array to RichTreeView items
 * - findNodeById: Locate node in tree by ID
 * - findNodesByIds: Locate multiple nodes in tree
 * - getNodePath: Get path from root to node
 * - isLeafNode: Check if node is at maximum depth
 * - optimizeSelection: Remove descendants when ancestor is selected
 * - getLevelLabel: Format field definition ID as display label
 * - shouldHideCheckbox: Determine if checkbox should be hidden in single-select mode
 */

import type { TreeNode, RichItem, HierarchyChain } from '../types/hierarchy';
import type { MasterDataValue } from '../types/mdm';
import type { FieldDefinition } from '../services/metadataService';
import { metadataService } from '../services/metadataService';

/**
 * Build hierarchy chain from root to target field
 *
 * Purpose: Traverse the hierarchy from the target field back to the root level
 * Logic:
 * - Starts with the target field definition
 * - Follows parent_fielddef_id links upward
 * - Caches field definitions to avoid redundant API calls
 * - Returns chain ordered from root (level 1) to target field
 *
 * Input:
 * @param fielddefId - The target field definition ID
 * @param tenantId - The tenant context
 * @param fieldDefCache - Cache map for field definitions
 *
 * Output:
 * @returns Promise<HierarchyChain> - Hierarchy chain with field definitions
 *
 * Exceptions:
 * - Throws error if field definition cannot be fetched
 * - Detects circular references and breaks to prevent infinite loops
 */
export async function buildHierarchyChain(
  fielddefId: string,
  tenantId: string,
  fieldDefCache: Map<string, FieldDefinition>
): Promise<HierarchyChain> {
  const chain: FieldDefinition[] = [];
  const visited = new Set<string>();

  let currentFieldDef: FieldDefinition | null = null;
  let currentFielddefId = fielddefId;

  while (currentFielddefId && !visited.has(currentFielddefId)) {
    visited.add(currentFielddefId);

    // Check cache first
    if (fieldDefCache.has(currentFielddefId)) {
      currentFieldDef = fieldDefCache.get(currentFielddefId)!;
    } else {
      try {
        const fetchedFieldDef = await metadataService.getFieldDefinition(
          currentFielddefId,
          tenantId
        );
        currentFieldDef = fetchedFieldDef;
        if (currentFieldDef) {
          fieldDefCache.set(currentFielddefId, currentFieldDef);
        }
      } catch (err: any) {
        console.error(
          `[hierarchyHelpers] Failed to fetch field definition ${currentFielddefId}:`,
          err
        );
        break;
      }
    }

    if (!currentFieldDef) break;
    chain.push(currentFieldDef);

    const parentFielddefId = currentFieldDef.hierarchy_config?.parent_fielddef_id;
    if (!parentFielddefId) break;

    currentFielddefId = parentFielddefId;
  }

  // Reverse to get root-to-target order
  const reversedChain = chain.reverse();

  return {
    fieldDefinitions: reversedChain,
    targetField: reversedChain[reversedChain.length - 1],
    maxLevel: reversedChain.length,
  };
}

/**
 * Convert MasterDataValue to TreeNode
 *
 * Purpose: Transform a master data value into a tree node structure
 * Logic: Creates a TreeNode with default loading state (not loaded, not loading)
 *
 * Input:
 * @param value - The master data value
 * @param level - Hierarchy level (0-based)
 * @param fielddefId - Field definition ID
 *
 * Output:
 * @returns TreeNode - Tree node representation
 */
export function valueToTreeNode(
  value: MasterDataValue,
  level: number,
  fielddefId: string
): TreeNode {
  return {
    id: value.id,
    value,
    children: [],
    fielddef_id: fielddefId,
    level,
    loaded: false,
    loading: false,
  };
}

/**
 * Build RichTreeView items from TreeNode array
 *
 * Purpose: Convert internal TreeNode structure to Material-UI RichTreeView format
 * Logic:
 * - Recursively builds nested item structure
 * - Adds loading placeholder for unloaded non-leaf nodes
 * - Filters out invalid nodes (missing ID or name)
 *
 * Input:
 * @param rootNodes - Array of root TreeNodes
 * @param hierarchyDepth - Maximum depth of hierarchy
 *
 * Output:
 * @returns RichItem[] - Array of tree items for RichTreeView
 */
export function buildTreeItems(rootNodes: TreeNode[], hierarchyDepth: number): RichItem[] {
  const buildItem = (node: TreeNode | null | undefined): RichItem | null => {
    if (!node) return null;
    if (!node.id || !node.value || !node.value.name) return null;

    let children: RichItem[] | undefined;

    // If node has loaded children, build them recursively
    if (node.children && node.children.length > 0) {
      const childItems = node.children
        .map((child) => buildItem(child))
        .filter((c): c is RichItem => c !== null);
      if (childItems.length > 0) {
        children = childItems;
      }
    }
    // If node hasn't been loaded yet but is not a leaf node,
    // add a placeholder child to show expand icon
    else if (!node.loaded && node.level < hierarchyDepth - 1) {
      children = [
        {
          id: `${node.id}-loading-placeholder`,
          label: 'Loading...',
        },
      ];
    }

    return {
      id: String(node.id),
      label: String(node.value.name),
      children,
    };
  };

  if (!rootNodes || rootNodes.length === 0) {
    return [];
  }

  const items: RichItem[] = [];
  rootNodes.forEach((root) => {
    const item = buildItem(root);
    if (item) {
      items.push(item);
    }
  });

  return items;
}

/**
 * Find node by ID in tree
 *
 * Purpose: Locate a specific node in the tree structure
 * Logic: Depth-first search through root nodes and their children
 *
 * Input:
 * @param nodeId - The node ID to find
 * @param rootNodes - Array of root TreeNodes
 *
 * Output:
 * @returns TreeNode | null - Found node or null
 */
export function findNodeById(nodeId: string, rootNodes: TreeNode[]): TreeNode | null {
  const search = (nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      if (node.children && node.children.length > 0) {
        const found = search(node.children);
        if (found) return found;
      }
    }
    return null;
  };

  return search(rootNodes);
}

/**
 * Find multiple nodes by IDs
 *
 * Purpose: Locate multiple nodes efficiently
 * Logic: Single traversal to find all requested nodes
 *
 * Input:
 * @param nodeIds - Array of node IDs to find
 * @param rootNodes - Array of root TreeNodes
 *
 * Output:
 * @returns TreeNode[] - Array of found nodes (may be partial)
 */
export function findNodesByIds(nodeIds: string[], rootNodes: TreeNode[]): TreeNode[] {
  const nodeIdSet = new Set(nodeIds);
  const found: TreeNode[] = [];

  const search = (nodes: TreeNode[]): void => {
    for (const node of nodes) {
      if (nodeIdSet.has(node.id)) {
        found.push(node);
        if (found.length === nodeIds.length) return; // Early exit
      }
      if (node.children && node.children.length > 0) {
        search(node.children);
      }
    }
  };

  search(rootNodes);
  return found;
}

/**
 * Get path from root to node
 *
 * Purpose: Build array of node IDs from root to target node
 * Logic: Depth-first search that tracks path during traversal
 *
 * Input:
 * @param nodeId - The target node ID
 * @param rootNodes - Array of root TreeNodes
 *
 * Output:
 * @returns string[] - Array of node IDs from root to target (empty if not found)
 */
export function getNodePath(nodeId: string, rootNodes: TreeNode[]): string[] {
  const search = (nodes: TreeNode[], path: string[]): string[] | null => {
    for (const node of nodes) {
      const currentPath = [...path, node.id];
      if (node.id === nodeId) return currentPath;
      if (node.children && node.children.length > 0) {
        const found = search(node.children, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  return search(rootNodes, []) || [];
}

/**
 * Check if node is a leaf node
 *
 * Purpose: Determine if a node is at the maximum hierarchy depth
 * Logic: Compares node's level against maximum hierarchy depth
 *
 * Input:
 * @param node - The tree node
 * @param hierarchyDepth - Maximum hierarchy depth
 *
 * Output:
 * @returns boolean - True if node is a leaf
 */
export function isLeafNode(node: TreeNode, hierarchyDepth: number): boolean {
  return node.level >= hierarchyDepth - 1;
}

/**
 * Optimize selection by removing descendants
 *
 * Purpose: Remove redundant selections when ancestor is selected
 * Logic:
 * - For each selected value, check if any other selected value is its ancestor
 * - Only keep values that are not descendants of other selected values
 * - This reduces storage and makes selection clearer
 *
 * Input:
 * @param selectedValues - Array of selected master data values
 *
 * Output:
 * @returns MasterDataValue[] - Optimized array with only top-level ancestors
 *
 * Example:
 * Input: [Americas, North America, United States]
 * Output: [Americas] (because North America and United States are descendants)
 */
export function optimizeSelection(selectedValues: MasterDataValue[]): MasterDataValue[] {
  const optimized: MasterDataValue[] = [];

  for (const val of selectedValues) {
    // Check if this value is a descendant of any other selected value
    const isDescendant = selectedValues.some((other) => {
      if (other.id === val.id) return false; // Don't compare with itself
      // Check if 'other' appears in val's hierarchy path
      return val.hierarchy_path?.some((item) => item.id === other.id);
    });

    // Only keep values that are not descendants
    if (!isDescendant) {
      optimized.push(val);
    }
  }

  return optimized;
}

/**
 * Get level label for display
 *
 * Purpose: Format field definition ID into a human-readable label
 * Logic: Removes 'fd_' prefix and replaces hyphens with spaces
 *
 * Input:
 * @param fielddefId - Field definition ID
 *
 * Output:
 * @returns string - Formatted label
 *
 * Example:
 * Input: 'fd_country-region'
 * Output: 'country region'
 */
export function getLevelLabel(fielddefId: string): string {
  return fielddefId.replace('fd_', '').replace(/-/g, ' ') || 'Unknown';
}

/**
 * May this node be selected? False for an ancestor a scoped (for_permission) list
 * returned only as a path to values in scope (ORCH-332).
 */
export function isNodeSelectable(node: TreeNode): boolean {
  return node.value.selectable !== false;
}

/**
 * Drop new picks of nodes that can't be selected (ORCH-332)
 *
 * Logic: an ancestor a scoped list returned only as a path can be opened, not picked,
 *        also when selection propagates up from its children. One that was already
 *        selected (on the record before the dialog opened) is kept until the user
 *        unticks it, so a value outside the form's scope is never dropped silently.
 * Input: picked - ids the tree reports selected; previous - ids selected before
 */
export function keepPickableSelections(
  picked: string[],
  nodeCache: Map<string, TreeNode>,
  previous: string[],
  locked: ReadonlySet<string> = new Set()
): string[] {
  const kept = picked.filter((id) => {
    const node = nodeCache.get(id);
    return !node || isNodeSelectable(node) || previous.includes(id);
  });
  // ORCH-400 decision 9: a value outside this user's access can't be unticked
  previous.forEach((id) => {
    if (locked.has(id) && !kept.includes(id)) kept.push(id);
  });
  return kept;
}

/**
 * Should hide checkbox for node
 *
 * Purpose: Determine if checkbox should be hidden in single-select mode
 * Logic:
 * - A non-selectable ancestor of a scoped tree (ORCH-332): always hide
 * - In multiselect mode: otherwise never hide checkboxes
 * - In single-select mode: hide checkboxes for nodes not at the field's level
 * - Field level is 1-based, node level is 0-based
 *
 * Input:
 * @param node - The tree node
 * @param fieldLevel - The field's hierarchy level (1-based)
 * @param isMultiselect - Whether multiselect is enabled
 *
 * Output:
 * @returns boolean - True if checkbox should be hidden
 *
 * Example:
 * - fieldLevel = 3 (Country field)
 * - node.level = 0 (Region node)
 * - Returns: true (hide checkbox because we're viewing Countries, not Regions)
 */
export function shouldHideCheckbox(
  node: TreeNode,
  fieldLevel: number,
  isMultiselect: boolean
): boolean {
  // ORCH-332: an ancestor kept only so a scoped tree stays navigable is never pickable
  if (!isNodeSelectable(node)) {
    return true;
  }
  if (isMultiselect) {
    return false; // Never hide in multiselect mode
  }

  // Hide checkbox if node is not at the field's level
  // fieldLevel is 1-based (1, 2, 3...), node.level is 0-based (0, 1, 2...)
  return node.level !== fieldLevel - 1;
}

/**
 * Validate selection for single-select mode
 *
 * Purpose: Ensure selected node is at the correct hierarchy level
 * Logic: Only nodes at the field's level are valid in single-select mode
 *
 * Input:
 * @param node - The tree node
 * @param fieldLevel - The field's hierarchy level (1-based)
 *
 * Output:
 * @returns boolean - True if selection is valid
 */
export function isValidSelection(node: TreeNode, fieldLevel: number): boolean {
  // fieldLevel is 1-based (1, 2, 3...), node.level is 0-based (0, 1, 2...)
  return node.level === fieldLevel - 1;
}

/**
 * Filter selection IDs by validity
 *
 * Purpose: Filter out invalid selections in single-select mode
 * Logic:
 * - Skip placeholder nodes (loading indicators)
 * - Check node exists in cache
 * - Validate node is at correct level
 *
 * Input:
 * @param selectedIds - Array of selected node IDs
 * @param nodeCache - Cache map of TreeNodes
 * @param fieldLevel - The field's hierarchy level (1-based)
 *
 * Output:
 * @returns string[] - Array of valid node IDs
 */
export function filterValidSelections(
  selectedIds: string[],
  nodeCache: Map<string, TreeNode>,
  fieldLevel: number
): string[] {
  return selectedIds.filter((id) => {
    // Skip placeholder nodes
    if (id.includes('-loading-placeholder')) {
      return false;
    }

    const node = nodeCache.get(id);
    if (!node) {
      return false;
    }

    // Only allow selection of nodes at the field's level
    return isValidSelection(node, fieldLevel);
  });
}

/**
 * Get display text for selected values
 *
 * Purpose: Format selected values for display in multiselect mode
 * Logic: Includes value name and level label
 *
 * Input:
 * @param selectedValues - Array of selected master data values
 *
 * Output:
 * @returns string - Comma-separated display text
 *
 * Example:
 * Input: [{ name: 'USA', fielddef_id: 'fd_country' }]
 * Output: 'USA (country)'
 */
export function getSelectedValuesDisplayText(selectedValues: MasterDataValue[]): string {
  return selectedValues.map((val) => `${val.name} (${getLevelLabel(val.fielddef_id)})`).join(', ');
}
